/* eslint-disable @typescript-eslint/no-explicit-any */
import api, { getFromDefaultApi } from "@/api";
import i18n from "@/plugins/i18n";
import { defineStore } from "pinia";
import { auth } from "@/utils/firebase";
import { signInWithEmailAndPassword } from "firebase/auth";
import { useDataStore } from "./dataStore";
import { Admin } from "@/interfaces/admin.interface";
import { useFilterStore } from "./filterStore";
import { applyRegionForLogin } from "@/lib/directory-client";
import {
  apiUrlForRegion,
  clearRegionApiUrl,
  getDashboardApiUrl,
  getDefaultDashboardApiUrl,
  getRegionalDashboardApiUrls,
  hasExplicitRegion,
  hasRegionApiUrl,
  markRegionReauthDone,
  setRegionApiUrl,
} from "@/lib/runtime-url-resolver";

/**
 * Read the admin uid out of a welcome-link registration token without verifying
 * it. The token is a JWT signed by the cluster that issued it; only that cluster
 * can verify it, so here it is used purely as a routing hint. Returns "" for
 * anything that does not decode.
 */
function uidFromRegistrationToken(token: string): string {
  try {
    const payload = String(token || "").split(".")[1];
    if (!payload) return "";
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const uid = JSON.parse(json)?.uid;
    return typeof uid === "string" ? uid : "";
  } catch {
    return "";
  }
}

/** A lookup the cluster answered with "not mine": unknown admin or a token it did not sign. */
function isNotOnThisCluster(error: any): boolean {
  const status = error?.response?.status;
  return status === 400 || status === 404;
}

/**
 * Fetch an admin from the DEFAULT (legacy/EU) cluster, or null if they are not there.
 * Used to confirm an internal admin exists on the default cluster before moving them
 * onto it. See login().
 */
async function fetchAdminFromDefaultApi(uid: string): Promise<Admin | null> {
  const body = await getFromDefaultApi<{ data?: Admin }>(
    `/v2/access/admin/single/${uid}`
  );
  return body?.data ?? null;
}

export const useUserStore = defineStore({
  id: "userStore",
  state: () => ({
    loader: {
      login: false,
      logout: false,
      registration: false,
      updateProfile: false,
      resetPassword: false,
      refreshSignupLink: false
    },
    user: null as Admin | null,
    /** Why the last login failed, when it is something the admin can act on. */
    loginError: "" as string,
    freshchatRestoreId: null as string | null,
  }),
  getters: {
    getUser(): Admin | null {
      return this.user;
    },
    /**
     * Returns an array of role names the user has.
     * Example: ['Class Group Admin', 'School Group Admin']
     */
    getAdminTypes(): string[] {
      return this.user?.roles.map((role) => role.name) || [];
    },
    /**
     * Returns an array of ClassGroup IDs the admin has access to.
     * Relevant for roles like 'Class Group Admin'.
     */
    getAdminClassGroupIds(): string[] {
      const classGroupIds: string[] = [];
      if (this.isClassGroupAdmin) {
        this.user?.roles.forEach((role) => {
          if (role.permissions) {
            role.permissions.forEach((permission) => {
              if (
                permission.resourceType === "ClassGroup" &&
                permission.resourceIds.length > 0
              ) {
                classGroupIds.push(...permission.resourceIds);
              }
            });
          }
        });
      }

      if (this.isSchoolAdmin) {
        // Return all class groups in the selected school and grade
        const filterStore = useFilterStore();
        const school = filterStore.selectedSchool;
        const grade = filterStore.selectedGrade;

        if (school && grade) {
          const classGroups = school.classes.filter(
            (cg) => cg.gradeLevel === grade
          );
          classGroupIds.push(...classGroups.map((cg) => cg._id));
        }
      }

      if (this.isInternalAdmin) {
        // Return all class groups in the selected school and grade
        const filterStore = useFilterStore();
        const school = filterStore.selectedSchool;
        const grade = filterStore.selectedGrade;

        if (school && grade) {
          const classGroups = school.classes.filter(
            (cg) => cg.gradeLevel === grade
          );
          classGroupIds.push(...classGroups.map((cg) => cg._id));
        }
      }

      return classGroupIds;
    },
    /**
     * Returns an array of SchoolGroup IDs the admin has access to.
     * Relevant for roles like 'School Group Admin'.
     */
    getAdminSchoolGroupIds(): string[] {
      const schoolGroupIds: string[] = [];
      this.user?.roles.forEach((role) => {
        if (role.permissions) {
          role.permissions.forEach((permission) => {
            if (
              permission.resourceType === "SchoolGroup" &&
              permission.resourceIds.length > 0
            ) {
              schoolGroupIds.push(...permission.resourceIds);
            }
          });
        }
      });
      return schoolGroupIds;
    },
    /**
     * Returns an array of School IDs the admin has access to.
     * Relevant for roles like 'School Admin'.
     */
    getAdminSchoolIds(): string[] {
      const schoolIds: string[] = [];
      this.user?.roles.forEach((role) => {
        if (role.permissions) {
          role.permissions.forEach((permission) => {
            if (
              permission.resourceType === "School" &&
              permission.resourceIds.length > 0
            ) {
              schoolIds.push(...permission.resourceIds);
            }
          });
        }
      });
      return schoolIds;
    },
    isInternalAdmin(): boolean {
      return this.getAdminTypes.includes("Internal Admin");
    },
    isSchoolGroupAdmin(): boolean {
      return this.getAdminTypes.includes("School Group Champion") || this.getAdminTypes.includes("School Group Admin");
    },
    isSchoolAdmin(): boolean {
      return this.getAdminTypes.includes("School Champion") || this.getAdminTypes.includes("School Admin");
    },
    isMultipleSchoolAdmin(): boolean {
      if (this.getAdminTypes.includes("School Champion")) {
        const schoolIds = this.user?.roles.flatMap((role) => 
          role.permissions
            .filter((permission) => permission.resourceType === "School")
            .flatMap((permission) => permission.resourceIds)
        ) || [];
        return schoolIds.length > 1;
      }
      return false;
    },
    isClassGroupAdmin(): boolean {
      return this.getAdminTypes.includes("Class Group Admin") || this.user?.designation === "Class Teacher" || this.user?.designation === "Class Group Admin";
    },
  },
  actions: {
    /**
     * Login user
     * @param loginDetails email, password, and an optional explicit region code
     *   from the login-page selector ("" / undefined = Automatic).
     */
    async login(loginDetails: { email: string; password: string; region?: string }) {
      this.loader.login = true;
      this.loginError = "";

      try {
        // Pre-auth: point the app's API at the admin's home cluster BEFORE any
        // auth/API call fires. A region explicitly chosen on the login page (the
        // fallback for internal admins the Directory tie-break can't disambiguate)
        // wins over auto-resolution and, being explicit, survives the internal-admin
        // default reset below. Otherwise auto-resolve via the Directory. Never
        // blocks login; a no-op in dev / when unresolved.
        if (loginDetails.region) {
          setRegionApiUrl(apiUrlForRegion(loginDetails.region), { explicit: true });
        } else {
          // Drop any stale explicit choice from a previous session before auto-resolving.
          clearRegionApiUrl();
          await applyRegionForLogin({ email: loginDetails.email });
        }

        const userCredential = await signInWithEmailAndPassword(
          auth,
          loginDetails.email,
          loginDetails.password
        );

        if (!userCredential) {
          throw new Error("Firebase user not found");
        }

        const userResponse = await api.get(
          `v2/access/admin/single/${userCredential.user?.uid}`
        );

        if (userResponse.status !== 200) {
          throw new Error("Failed to fetch user data from API.");
        }

        void api
          .post("/v2/access/activity-logs/event", {
            entityType: "admin",
            actionType: "login",
            entityId: userResponse.data?.data?._id,
            details: {
              note: `Admin login - ${userResponse.data?.data?.firstName || ""} ${userResponse.data?.data?.lastName || ""}`.trim(),
            },
          })
          .catch((error) => {
            console.error("Failed to log user login event:", error);
          });

        const userData = userResponse.data.data as Admin;
        this.user = userData;
        this.freshchatRestoreId =
          typeof userData?.freshchat?.restoreId === "string"
            ? userData.freshchat.restoreId
            : null;

        // Internal admins work the main (default/EU) dataset, but the Directory
        // tie-break prefers the REGIONAL cluster for anyone present in both, so it
        // resolves them to the region. Move them back to the default.
        //
        // Only after CONFIRMING they exist there. Not every internal admin does:
        // of 47 internal admins, 28 are in both clusters (they should be on the
        // default) but 19 exist ONLY in the regional cluster. Clearing the region
        // unconditionally sent those 19 to a cluster with no admin record for them,
        // which fails their profile load and empties the dashboard.
        //
        // An explicitly chosen region (the login-page toggle) always wins, and a
        // failed probe is treated as "stay put" so a blip can never strand anyone.
        if (this.isInternalAdmin && !hasExplicitRegion() && hasRegionApiUrl()) {
          const defaultRecord = await fetchAdminFromDefaultApi(
            userCredential.user.uid
          );
          if (defaultRecord) {
            clearRegionApiUrl();
            // Adopt the default cluster's record: it is the one whose roles and
            // permissions match the data we are about to load.
            this.user = defaultRecord;
          }
        }

        // This browser has now authenticated with region routing applied, so it must
        // never be caught by the one-time forced re-auth in the router guard. Stamped
        // here rather than on first paint so a brand-new login is never signed out.
        markRegionReauthDone();

        // Fetch initial data after login
        const dataStore = useDataStore();
        await dataStore.fetchInitialData(true);
      } catch (error: any) {
        if (error.code) {
          switch (error.code) {
            case "auth/wrong-password":
              alert(
                "The password is invalid or the user does not have a password."
              );
              break;

            case "auth/user-not-found":
              alert(
                "There is no user record corresponding to this identifier. The user may have been deleted."
              );
              break;

            case "auth/too-many-requests":
              alert(
                "Too many unsuccessful login attempts. Please try again later."
              );
              break;

            // API Errors
            case "ERR_BAD_REQUEST":
              if (error.response.status === 404) {
                alert("User not found. Please try again.");
              } else {
                alert("An unexpected error occurred. Please try again.");
              }
              break;

            default:
              alert("An unexpected error occurred. Please try again.");
              break;
          }
        } else {
          console.error("Login failed:", error);
        }

        // No school to show because theirs moved region: say so, rather than
        // let the login page report a wrong password.
        const moved = useFilterStore().filterOptions?.movedSchools;
        if (moved && moved.length) {
          const where = moved[0].region ? moved[0].region.toUpperCase() : "another region";
          this.loginError = i18n.global
            .t("login_view.school_moved_account_not_moved", { school: moved[0].name, region: where })
            .toString();
        }

        this.user = null;
        this.freshchatRestoreId = null;
        return false;
      } finally {
        this.loader.login = false;
      }
      return true;
    },

    /**
     *Get user id
     *@param userId
     */
    async googleLogin(userId: string) {
      try {
        const userResponse = await api.get(`v2/access/admin/single/${userId}`);
        console.log("userResponse: ", userResponse);

        if (userResponse.status !== 200) {
          throw new Error("Failed to fetch user data from API.");
        }

        const userData = userResponse.data.data as Admin;
        this.user = userData;
        this.freshchatRestoreId =
          typeof userData?.freshchat?.restoreId === "string"
            ? userData.freshchat.restoreId
            : null;

        // Fetch initial data after login
        const dataStore = useDataStore();
        await dataStore.fetchInitialData(true);
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          switch (error.code) {
            case "auth/wrong-password":
              alert(
                "The password is invalid or the user does not have a password."
              );
              break;

            case "auth/user-not-found":
              alert(
                "There is no user record corresponding to this identifier. The user may have been deleted."
              );
              break;

            case "auth/too-many-requests":
              alert(
                "Too many unsuccessful login attempts. Please try again later."
              );
              break;

            // API Errors
            case "ERR_BAD_REQUEST":
              if (error.response.status === 404) {
                alert("User not found. Please try again.");
              } else {
                alert("An unexpected error occurred. Please try again.");
              }
              break;

            default:
              alert("An unexpected error occurred. Please try again.");
              break;
          }
        }

        this.user = null;
        this.freshchatRestoreId = null;
        return false;
      }

      return true;
    },
    
    /**
     * Login as admin
     * @param userId
     */
    async loginAsAdmin(userId: string) {
      try {
        const userResponse = await api.get(`v2/access/admin/single/${userId}`);
        if (userResponse.status !== 200) {
          throw new Error("Admin user not found.");
        }
        const userData = userResponse.data.data as Admin;
        this.user = userData;
        this.freshchatRestoreId =
          typeof userData?.freshchat?.restoreId === "string"
            ? userData.freshchat.restoreId
            : null;
        const dataStore = useDataStore();
        await dataStore.fetchInitialData(true);
      } catch (error: any) {
        console.log("Error: ", error);
      }
    },

    /**
     * Unauthenticated - Reset password
     * @param email user's email address
     */
    async resetPassword(email: string) {
      // Firebase invalidates an earlier reset code when a newer one is issued.
      // Keep repeated submits from sending competing emails while one is in flight.
      if (this.loader.resetPassword) {
        return;
      }

      this.loader.resetPassword = true;
      try {
        const response = await api.post("v2/access/admin/request-password-reset", {
          email,
        });
        if (response.status !== 200) {
          throw new Error("Failed to send password reset email.");
        }
        alert("Password reset email sent successfully.");
      } catch (error: any) {
        console.error("Error sending password reset email: ", error);
        if (error.response) {
          console.error("Error response status:", error.response.status);
          console.error("Error response data:", error.response.data);
        }
        alert("Failed to send password reset email. Please try again.");
      } finally {
        this.loader.resetPassword = false;
      }
    },

    /**
     * Reset password for authenticated users
     * @param email user's email address
     */
    async resetAuthPassword(email: string) {
      this.loader.resetPassword = true;
      try {
        const response = await api.post("v2/access/admin/admin-password-reset", {
          email,
        });
        if (response.status !== 200) {
          throw new Error("Failed to reset password.");
        }
        return response;
      } catch (error: any) {
        console.error("Error resetting password: ", error);
        if (error.response) {
          console.error("Error response status:", error.response.status);
          console.error("Error response data:", error.response.data);
        }
        return error;
      } finally {
        this.loader.resetPassword = false;
      }
    },

    /**
     * Get admin account
     * @param userToken
     */
    async getAdminAccount(userToken: string) {
      const path = `v2/access/admin/get-initialised-admin?registrationToken=${userToken}`;

      // The welcome link is opened before anyone has logged in, so nothing has
      // routed this browser yet. An invited admin exists only on the cluster that
      // invited them, and every other cluster answers "invalid or expired"
      // (NLM-Test #204: every new AU teacher). Route by the uid in the token the
      // same way login routes by email, starting from the default so a region
      // left over from a previous session cannot misdirect the lookup.
      clearRegionApiUrl();
      const uid = uidFromRegistrationToken(userToken);
      if (uid) {
        await applyRegionForLogin({ uid });
      }

      try {
        const userResponse = await api.get(path);

        if (userResponse.status !== 200) {
          throw new Error("Failed to fetch user data from API.");
        }

        return userResponse.data.data;
      } catch (error: any) {
        if (!isNotOnThisCluster(error)) {
          console.log("Error: ", error);
          throw error;
        }

        // The Directory was unavailable or could not place the uid. Ask each
        // other cluster directly and stay on whichever one knows the token, so
        // completing registration writes to the same place.
        const tried = getDashboardApiUrl();
        const candidates = [getDefaultDashboardApiUrl(), ...getRegionalDashboardApiUrls()].filter(
          (url, index, all) => url && url !== tried && all.indexOf(url) === index
        );
        for (const baseURL of candidates) {
          try {
            const response = await api.get(path, { baseURL });
            if (response.status === 200) {
              if (baseURL === getDefaultDashboardApiUrl()) {
                clearRegionApiUrl();
              } else {
                setRegionApiUrl(baseURL);
              }
              return response.data.data;
            }
          } catch (probeError: any) {
            if (!isNotOnThisCluster(probeError)) {
              console.log("Error: ", probeError);
            }
          }
        }

        console.log("Error: ", error);
        throw error;
      }
    },

    async completeRegistration(userDetails: {
      token: string;
      userPassword: string;
      courtesyTitle: string;
    }) {
      this.loader.registration = true;

      try {
        const userResponse = await api.post(
          `v2/access/admin/complete-registration?registrationToken=${userDetails.token}`,
          {
            password: userDetails.userPassword,
            courtesyTitle: userDetails.courtesyTitle,
          }
        );

        if (userResponse.status !== 200) {
          throw new Error("Failed to fetch user data from API.");
        }

        const userData = userResponse.data.data as Admin;
        this.user = userData;
        this.freshchatRestoreId =
          typeof userData?.freshchat?.restoreId === "string"
            ? userData.freshchat.restoreId
            : null;
      } catch (error: any) {
        console.log("Error: ", error);

        if (error.code) {
          switch (error.code) {
            case "ERR_BAD_REQUEST":
              if (error.response.status === 404) {
                alert("User not found. Please try again.");
              } else {
                alert("An unexpected error occurred. Please try again.");
              }
              break;

            default:
              alert("An unexpected error occurred. Please try again.");
              break;
          }
        }

        this.freshchatRestoreId = null;
        return false;
      } finally {
        this.loader.registration = false;
      }

      return true;
    },

    async selfRefreshRegistrationToken({ oldToken, email }: { oldToken: string, email: string }) {
      try {
        this.loader.refreshSignupLink = true;
        // An expired link may no longer resolve by its token, so route by the
        // email the admin typed, as login does.
        clearRegionApiUrl();
        await applyRegionForLogin({ email });
        const { data } = await api.post(
          "/v2/access/admin/self-refresh-registration-link",
          {
            oldRegistrationToken: oldToken,
            email
          }
        );
        this.loader.refreshSignupLink = false;
        return data;
      } catch (err) {
        console.log(err)
        throw err;
      } finally {
        this.loader.refreshSignupLink = false;
      }
    },

    /**
     * One-time forced re-authentication so region routing takes effect on sessions
     * that predate it.
     *
     * Region routing is applied only inside login(). A session created before it
     * shipped has no region and nothing would ever give it one, so it keeps working
     * the default (legacy) cluster indefinitely: not an outage, since that cluster
     * still holds every admin, but writes land on a cluster the regional copy is
     * drifting away from. Signing everyone out once puts every session through
     * login(), which is the single authority on routing (auto-resolve, the explicit
     * region toggle, and the internal-admin default reset all live there).
     *
     * A plain logout() is not enough. Nineteen Pinia stores persist to localStorage
     * (schools, licences, learners, mastery, concepts, subjects, filters, ...) and
     * logout() clears only the filters and the user. Anything left behind was fetched
     * from the previous cluster, so the dashboard would render one cluster's data
     * while writing to another. Purging storage and doing a full document navigation
     * is the only way to guarantee nothing survives, in memory or on disk.
     */
    async forceRegionReauth(): Promise<void> {
      try {
        await auth.signOut();
      } catch {
        /* best-effort: the purge below is what actually protects the next session */
      }
      try {
        // Broad on purpose - this is "start again". It also drops the persisted
        // Firebase auth state, which we have just invalidated.
        window.localStorage.clear();
        window.sessionStorage.clear();
      } catch {
        /* ignore storage errors */
      }
      // Re-stamp AFTER the purge so the marker is the only survivor. This is what
      // makes the forced logout un-repeatable, even if the next login never happens.
      markRegionReauthDone();
      this.user = null;
      this.freshchatRestoreId = null;
      // Full document navigation, not router.push: module-level state (the axios
      // base URL, every store instance) has to be rebuilt from scratch.
      window.location.assign("/auth/login?regionChanged=1");
    },

    /**
     * Logout user
     */
    async logout() {
      const filterStore = useFilterStore();

      try {
        await auth.signOut();
        filterStore.resetFilters();
        this.user = null;
        this.freshchatRestoreId = null;
        return true;
      } catch (error) {
        console.log("Error: ", error);
        return false;
      }
    },

    /**
     * Update user profile
     * @param updatedData Object containing updated user data
     */
    async updateProfile(updatedData: Partial<Admin>) {
      this.loader.updateProfile = true;

      try {
        const currentUser = this.user;

        if (!currentUser) {
          throw new Error("No user is logged in.");
        }

        // Send updated data to your backend API
        const response = await api.put(
          `v2/access/admin/update/${currentUser._id}`,
          updatedData
        );

        if (response.status !== 200) {
          throw new Error("Failed to update user data on API.");
        }

        // Update the user in the store
        const updatedUser = response.data.data as Admin;
        this.user = updatedUser;
        if (updatedUser?.freshchat) {
          this.freshchatRestoreId =
            typeof updatedUser.freshchat.restoreId === "string"
              ? updatedUser.freshchat.restoreId
              : null;
        }

        return true;
      } catch (error: any) {
        // Handle specific Firebase Auth errors
        if (error.code === "auth/wrong-password") {
          alert("Incorrect password. Please try again.");
        } else if (error.code === "auth/email-already-in-use") {
          alert("The email address is already in use by another account.");
        } else {
          alert("Failed to update profile.");
        }

        return false;
      } finally {
        this.loader.updateProfile = false;
      }
    },

    async syncFreshchatRestoreId(restoreId: string) {
      if (!restoreId || !this.user?._id) {
        return;
      }

      if (this.freshchatRestoreId === restoreId) {
        return;
      }

      try {
        await api.post("/v2/access/admin/freshchat", {
          userId: this.user._id,
          restoreId,
        });
        this.freshchatRestoreId = restoreId;
        if (this.user) {
          this.user = {
            ...this.user,
            freshchat: {
              ...(this.user.freshchat ?? {}),
              restoreId,
            },
          } as Admin;
        }
      } catch (error) {
        console.error("Failed to sync Freshchat restore ID:", error);
      }
    },
  },
  persist: true,
});
